import { useViewport } from 'contexts/viewport';
import { Trans, useTranslation } from 'react-i18next';
import {
    Box,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    styled,
    Text,
    TextArea,
    Tooltip,
} from 'shell/components';
import { getLanguageLevelDescriptionText, getToneDescriptionText, getTooltipComponent } from 'utils/helper';

const FormativeForm = ({ form, setForm, errors }) => {
    const { t } = useTranslation('assignment');
    const { device } = useViewport();

    if (!form) return null;

    return (
        <>
            <Paper sx={{ p: 5 }} variant={'outlined'}>
                <Stack spacing={3}>
                    <Text id={'topic-label'} variant={'p1'} weight={'semibold'}>
                        {t('formative-feedback.assignment-title', 'What is the main focus of your assignment?')}
                        <Text color={'error'} component={'span'} variant={'p1'}>
                            *
                        </Text>
                    </Text>
                    <Text>
                        <Trans
                            i18nKey='assignment:formative-feedback.assignment-tip'
                            components={{ italics: <Text component={'i'} /> }}
                            defaults={
                                'Tip: <italics>(In one sentence)</italics> Specify the topic you’ve chosen to focus on.'
                            }
                        />
                    </Text>
                    <TextArea
                        aria-labelledby={'topic-label'}
                        error={Boolean(errors.topic)}
                        helperText={errors.topic}
                        helperTextPosition={'end'}
                        placeholder={t(
                            'formative-feedback.assignment-placeholder',
                            'For example: My topic explores the growth potential of Mainland Soda Kombucha in the beverage market.'
                        )}
                        rows={5}
                        value={form.topic}
                        onChange={(e) => setForm((current) => ({ ...current, topic: e.target.value }))}
                    />
                </Stack>
            </Paper>
            {form.settings && (
                <Stack spacing={2}>
                    <Text variant={'p1'} weight={'medium'}>
                        <Trans
                            i18nKey={'assignment:formative-feedback.structure-title'}
                            components={{ italics: <Text component={'i'} /> }}
                            defaults={'Feedback Structure <italics>(Optional)</italics>'}
                        />
                    </Text>
                    <Text>
                        {t(
                            'formative-feedback.structure-description',
                            'Customize your feedback preferences. Instructor defaults are applied, but you can tailor them to your needs.'
                        )}
                    </Text>
                    <Paper sx={{ p: 5 }} variant={'outlined'}>
                        <Stack spacing={3}>
                            <CustomSelect
                                label={
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <Text variant={'p1'} weight={'semibold'}>
                                            {t(
                                                'feedback-section.settings.suggestion-length',
                                                'Length of Each Suggestion'
                                            )}
                                        </Text>
                                        <Tooltip
                                            content={getTooltipComponent(
                                                t(
                                                    'feedback-section.settings.suggestion-length',
                                                    'Length of Each Suggestion'
                                                ),
                                                t(
                                                    'feedback-section.settings.suggestion-length-tooltip',
                                                    'Refers to the length of each unique suggestion provided to the student. For example, in cases of simple assignments where in-depth feedback is not crucial, choosing brief suggestions might help avoid overburdening the student.'
                                                )
                                            )}
                                            type={'info'}
                                        />
                                    </Stack>
                                }
                                labelId={'suggestion-length-select'}
                                value={form.settings.suggestionLength}
                                onChange={(e) => {
                                    setForm((current) => ({
                                        ...current,
                                        settings: {
                                            ...current.settings,
                                            suggestionLength: e.target.value,
                                        },
                                    }));
                                }}
                            >
                                <MenuItem value={'<25'}>
                                    {t('common:word-length.under-word-count', 'Under {{wordCount}} words', {
                                        wordCount: 25,
                                    })}
                                </MenuItem>
                                <MenuItem value={'25-50'}>
                                    {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                        wordCountRange: '25-50',
                                    })}
                                </MenuItem>
                                <MenuItem value={'50-150'}>
                                    {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                        wordCountRange: '50-150',
                                    })}
                                </MenuItem>
                                <MenuItem value={'150-300'}>
                                    {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                        wordCountRange: '150-300',
                                    })}
                                </MenuItem>
                                <MenuItem value={'300-450'}>
                                    {t('common:word-length.word-count-range', '{{wordCountRange}} words', {
                                        wordCountRange: '300-450',
                                    })}
                                </MenuItem>
                            </CustomSelect>
                            <Stack spacing={3}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        {t('feedback-settings.language-level.title', 'Language Level')}
                                    </Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t('feedback-settings.language-level.title', 'Language Level'),
                                            t(
                                                'feedback-settings.language-level.feedback-tooltip',
                                                'Defines the language proficiency at which feedback is communicated. Each level is assessed based on the Flesch-Kincaid Grade Level Scale.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                                <CustomRadioGroup
                                    aria-label={'language level'}
                                    row={device !== 'mobile'}
                                    value={form.settings.languageLevel}
                                    onChange={(e) => {
                                        setForm((current) => ({
                                            ...current,
                                            settings: {
                                                ...current.settings,
                                                languageLevel: e.target.value,
                                            },
                                        }));
                                    }}
                                >
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t(
                                                'feedback-settings.language-level.middle-school-label',
                                                'Middle School'
                                            )}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'middle-school'}
                                        />
                                    </CustomStack>
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t(
                                                'feedback-settings.language-level.high-school-label',
                                                'High School'
                                            )}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'high-school'}
                                        />
                                    </CustomStack>
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t(
                                                'feedback-settings.language-level.undergraduate-label',
                                                'Undergraduate'
                                            )}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'undergraduate'}
                                        />
                                    </CustomStack>
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t('feedback-settings.language-level.graduate-label', 'Graduate +')}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'graduate'}
                                        />
                                    </CustomStack>
                                </CustomRadioGroup>
                                <Box sx={{ ...(device !== 'mobile' && { height: '32px' }) }}>
                                    <Text>{getLanguageLevelDescriptionText(form.settings.languageLevel, t)}</Text>
                                </Box>
                            </Stack>
                            <Stack spacing={3}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text variant={'p1'} weight={'semibold'}>
                                        {t('feedback-settings.tone.feedback-title', 'Feedback Tone')}
                                    </Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            t('feedback-settings.tone.feedback-title', 'Feedback Tone'),
                                            t(
                                                'feedback-settings.tone.feedback-tooltip',
                                                'Describes how feedback is articulated, and whether it leans towards being supportive or critical, depending on the assignment’s demands and the student audience involved.'
                                            )
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                                <CustomRadioGroup
                                    aria-label={'feedback tone'}
                                    row={device !== 'mobile'}
                                    value={form.settings.tone}
                                    onChange={(e) => {
                                        setForm((current) => ({
                                            ...current,
                                            settings: {
                                                ...current.settings,
                                                tone: e.target.value,
                                            },
                                        }));
                                    }}
                                >
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t('feedback-settings.tone.very-supportive-label', 'Very Supportive')}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'very-supportive'}
                                        />
                                    </CustomStack>
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t('feedback-settings.tone.supportive-label', 'Supportive')}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'supportive'}
                                        />
                                    </CustomStack>
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t('feedback-settings.tone.balanced-label', 'Balanced')}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'balanced'}
                                        />
                                    </CustomStack>
                                    <CustomStack alignItems={device === 'mobile' ? 'flex-start' : 'center'}>
                                        <Radio
                                            label={t('feedback-settings.tone.critical-label', 'Critical')}
                                            labelPlacement={device === 'mobile' ? 'end' : 'bottom'}
                                            value={'critical'}
                                        />
                                    </CustomStack>
                                </CustomRadioGroup>
                                <Text>{getToneDescriptionText(form.settings.tone, t)}</Text>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>
            )}
        </>
    );
};

const CustomSelect = styled(Select)(() => {
    return {
        '& > .MuiInputBase-root': {
            width: '150px',
        },
    };
});

const CustomRadioGroup = styled(RadioGroup)(() => {
    return {
        justifyContent: 'space-between',
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        width: '150px',
        '& > .MuiFormControlLabel-root': {
            width: 'fit-content',
        },
    };
});

export default FormativeForm;
